import { Link } from 'react-router-dom';
import { useLanguage } from '../core/hooks/use-language';
import { useTranslation } from '../core/hooks/use-translation';

/**
 * The page for a URL this site does not answer.
 *
 * **It offers three ways on rather than one.** A visitor reaching this has usually followed a link
 * that has moved, and the three things they could have been looking for are the site itself, an
 * article, and a consultation — so the page says which those are instead of returning them to the
 * home page to start again.
 *
 * The routes this can be reached through are worth remembering: `/book`, `/policy`, `/legal` and
 * `/blog` are all answerable unprefixed because the live static site answers them, and they
 * redirect rather than land here. Anything that arrives here is genuinely unknown.
 */
export function NotFoundPage() {
    const { t } = useTranslation();
    const language = useLanguage();

    return (
        <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-24 text-center">
            <p className="text-6xl font-black text-brand-600/20 dark:text-brand-400/20">404</p>

            <h1 className="mt-2 text-2xl font-extrabold text-slate-900 dark:text-slate-100">
                {t('not_found.title')}
            </h1>
            <p className="mt-3 text-slate-600 dark:text-slate-400">{t('not_found.body')}</p>

            <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link
                    to={`/${language}`}
                    className="rounded-xl bg-brand-600 px-5 py-3 font-semibold text-white transition hover:bg-brand-700"
                >
                    {t('not_found.home')}
                </Link>
                <Link
                    to={`/${language}/blog`}
                    className="rounded-xl border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:border-slate-400 dark:border-slate-700 dark:text-slate-200"
                >
                    {t('nav.blog')}
                </Link>
                <Link
                    to={`/${language}/book`}
                    className="rounded-xl border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:border-slate-400 dark:border-slate-700 dark:text-slate-200"
                >
                    {t('site.nav.cta')}
                </Link>
            </div>
        </div>
    );
}
